import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DatePicker, Input, message } from "antd";
import MainLayout from "../../layout/MainLayout";
import { Button1, Button2, Select, TagInput } from "../index";
import infoIcon from "../../assets/icons/info-icon.png";
import uploadIcon from "../../assets/icons/upload-icon.png";
import calendarIcon from "../../assets/icons/calendar-icon.png";
import plusIcon from "../../assets/icons/plus-circle-white.png";
import { useAccounts, useGroups } from "../../data/live";
import { socialPaymentsApi } from "../../api/endpoints";
import QuickActionSelect from "../quick-action-select/QuickActionSelect";

const CATEGORIES = ["Travel", "Food & Drink", "Bills", "Entertainment", "Other"];

const AddExpenses = () => {
  const navigate = useNavigate();
  const { accounts } = useAccounts();
  const { items: groups } = useGroups();

  const [groupId, setGroupId] = useState("");
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("AUD");
  const [category, setCategory] = useState("");
  const [date, setDate] = useState(null);
  const [splitType, setSplitType] = useState("equally");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (accounts.length && !currency) {
      const def = accounts.find((a) => a.isDefault) || accounts[0];
      setCurrency(def.currency);
    }
  }, [accounts, currency]);

  const selectedGroup = groups.find((g) => g.id === groupId);
  const memberIds = Array.isArray(selectedGroup?.members)
    ? selectedGroup.members
    : [];
  const memberCount = memberIds.length;
  const perPerson =
    memberCount > 0 && amount
      ? (Number(amount) / (memberCount + 1)).toFixed(2)
      : "0.00";

  const handleCreate = async () => {
    setError(null);
    if (!groupId) return setError("Choose a group");
    if (!title.trim()) return setError("Enter expense title");
    if (!amount || Number(amount) <= 0) return setError("Enter a valid amount");

    setSubmitting(true);
    try {
      const splits = memberIds.map((userId) => ({
        userId,
        amountOwed: perPerson,
      }));

      await socialPaymentsApi.addExpense({
        groupId,
        description: title.trim(),
        amount: String(amount),
        currency,
        date: date || new Date().toISOString(),
        category: category || null,
        tags: [],
        splits,
      });
      message.success("Expense added");
      navigate("/split-payment");
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to add expense");
    } finally {
      setSubmitting(false);
    }
  };

  const currencyOptions = (
    accounts.length
      ? [...new Set(accounts.map((a) => a.currency))]
      : ["AUD", "USD", "EUR", "GBP"]
  ).map((c) => ({ name: c, value: c }));

  const groupOptions = groups.map((g) => ({
    name: g.name || `Group ${g.id?.slice(0, 6)}`,
    value: g.id,
  }));

  return (
    <>
      <MainLayout NavName="Split Payment" expand={false}>
        <div>
          <div className="container mt-5">
            <div className="row">
              <div className="d-flex justify-content-between align-items-center flex-wrap flex-md-nowrap gap-2 gap-md-0">
                <span className="fs-5 fw-semibold">Add Expense</span>
                <div className="d-flex align-items-center gap-2">
                  <Button1
                    buttonName={"Request Payment"}
                    className={"btn-outline-success"}
                  />
                  <QuickActionSelect />
                </div>
              </div>

              <div className="col-12 px-4 mt-3">
                <div className="row border rounded-3 p-3">
                  <span className="fs-5 fw-semibold">Expense Details</span>

                  <div className="col-lg-6 mt-3">
                    <div className="d-flex flex-column gap-3 pe-3">
                      <div className="d-flex flex-column gap-2">
                        <label className="fs12px">Group</label>
                        <Select
                          className="p-3"
                          defaultValue={
                            groups.length === 0
                              ? "No groups yet, create one first"
                              : "Select group"
                          }
                          value={groupId}
                          options={groupOptions}
                          onChange={(e) => setGroupId(e.target.value)}
                        />
                      </div>

                      <div className="d-flex flex-column gap-2">
                        <label className="fs12px">Expense title</label>
                        <Input
                          placeholder="Winter trip to Europe"
                          className="p-3"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                        />
                      </div>

                      <div className="d-flex flex-column gap-2">
                        <div className="d-flex gap-3">
                          <div className="d-flex flex-column gap-2 w-75">
                            <label className="fs12px">Amount</label>
                            <Input
                              className="p-3 w-100"
                              placeholder="0.00"
                              value={amount}
                              onChange={(e) => setAmount(e.target.value)}
                            />
                          </div>
                          <div className="d-flex flex-column gap-2">
                            <label className="fs12px">Currency</label>
                            <Select
                              value={currency}
                              defaultValue="AUD"
                              options={currencyOptions}
                              className="p-3"
                              selectStyle={{ width: "100%" }}
                              onChange={(e) => setCurrency(e.target.value)}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="d-flex flex-column gap-2">
                        <label className="fs12px">Split type</label>
                        <Select
                          className="p-3"
                          value={splitType}
                          options={[
                            { name: "Equally", value: "equally" },
                            { name: "Unequally", value: "unequally" },
                          ]}
                          onChange={(e) => setSplitType(e.target.value)}
                        />
                        <div className="d-flex gap-2 align-items-center">
                          <img src={infoIcon} alt="info" />
                          <span className="fs12px" style={{ color: "#7D7D7D" }}>
                            {memberCount > 0
                              ? `${perPerson} ${currency} per person (${
                                  memberCount + 1
                                } people)`
                              : "Pick a group with members"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-6 mt-3">
                    <div className="d-flex gap-3 flex-column ps-3">
                      <div className="d-flex flex-column gap-2">
                        <label className="fs12px">Receipt</label>
                        <Input
                          placeholder="Upload or drag files here"
                          className="p-3"
                          suffix={<img src={uploadIcon} alt="upload" />}
                        />
                      </div>

                      <div className="d-flex flex-column gap-2">
                        <label className="fs12px">Date</label>
                        <DatePicker
                          placeholder="Select date"
                          className="p-3"
                          suffixIcon={<img src={calendarIcon} alt="" />}
                          onChange={(d) => setDate(d?.toISOString() || null)}
                        />
                      </div>

                      <div className="d-flex flex-column gap-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <label className="fs12px">Group</label>
                          <Button2
                            buttonName={"New Group"}
                            icon={<img src={plusIcon} alt="add" />}
                            className={"btn-dark py-1 px-3"}
                            divClassName={"gap-2"}
                            onClick={() => navigate("/create-group")}
                          />
                        </div>
                        <Select
                          defaultValue="Select group"
                          className="p-3"
                          value={groupId}
                          options={groupOptions}
                          onChange={(e) => setGroupId(e.target.value)}
                        />
                      </div>

                      <div className="d-flex flex-column gap-2">
                        <label className="fs12px">Category</label>
                        <Select
                          defaultValue="Choose category"
                          className="p-3"
                          value={category}
                          options={CATEGORIES.map((c) => ({ name: c, value: c }))}
                          onChange={(e) => setCategory(e.target.value)}
                        />
                      </div>

                      <div className="d-flex flex-column gap-2">
                        <label className="fs12px">Tags</label>
                        <TagInput
                          className="border py-3"
                          inputBgColor="transparent"
                          tagColor="#1bbb6b"
                          tagTextColor="#000"
                          limit={10}
                          placeholder="Add up to 10 Tags"
                        />
                      </div>
                    </div>
                  </div>

                  {error && (
                    <div className="col-12 mt-2">
                      <span className="fs12px" style={{ color: "#FC555B" }}>
                        {error}
                      </span>
                    </div>
                  )}

                  <div className="d-flex justify-content-end align-items-center gap-3 mt-5">
                    <Button1 buttonName={"Share"} className={"border"} />
                    <Button1
                      buttonName={"Save as Draft"}
                      className={"btn-outline-success"}
                      disabled={submitting}
                    />
                    <Button1
                      buttonName={submitting ? "Creating..." : "Create"}
                      className={"btn-dark"}
                      onClick={handleCreate}
                      disabled={submitting}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    </>
  );
};

export default AddExpenses;
